import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import GenDrugContinueInterfaceLogSerializer from "issara-sdk/types/GenDrugContinueIssueLogSerializer_apps_TPD";

// API
import DrugContinueLostList from "issara-sdk/apis/DrugContinueLostList_apps_TPD";
import GenDrugContinueInterfaceData from "issara-sdk/apis/GenDrugContinueIssueByPlanItem_apps_TPD";

export type Props = {

}

export type State = Partial<{
  // seq
  GenerateContinueByPlanSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    searchContinue: FilterSearchType;
    generateContinueData: any[];
    continueList: GenDrugContinueInterfaceLogSerializer[];
    paginatedItems: PaginatedItemsType;
  }>;
}>;

export type FilterSearchType = Partial<{
  created_date: string;
  hn: string;
  division: number | string;
}>;

type PaginatedItemsType = Partial<{
  items: GenDrugContinueInterfaceLogSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence Type
type SeqState = {
  sequence: "GenerateContinueByPlan";
  restart?: boolean;
  clear?: boolean;
};

// Action Type
type ActionType =
  | { action: "SEARCH"; page?: number; pageSize?: number }
  // | { action: "FETCH"; }
  | { action: "CLEAR" }
  | { action: "GENERATE"; selectedItems: any[] };

type SeqAct = ActionType & SeqState;

type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  GenerateContinueByPlanSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

export const DataInitial = {};

export const CARD_NAME = "CardGenerateContinueByPlan";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  GENERATE: "GENERATE",
  // FETCH: "FETCH",
  CLEAR: "CLEAR",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const INIT_DATA: FilterSearchType = {
  created_date: "",
  hn: "",
  division: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

const Masters = [["division", {}]] as const;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      GenerateContinueByPlanSequence: {
        ...state.GenerateContinueByPlanSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    }
    // () => Action(controller, { ...params, action: "SEARCH" })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchContinue,
    // [ACTIONS.FETCH]: HandleFetchContinue,
    [ACTIONS.GENERATE]: HandleGenerateContinue,
    [ACTIONS.CLEAR]: HandleClearContinue,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

// export const HandleFetchContinue: Handler<Params<"FETCH">> = async (controller, params) => {
//   const state = controller.getState();

//   const paginatedItems = state.GenerateContinueByPlanSequence?.paginatedItems;
//   const page = paginatedItems?.page || 0;
//   const pageSize = paginatedItems?.pageSize || LIST_LIMIT;
//   const offset = page * pageSize;

//   let data = {
//     limit: pageSize,
//     offset,
//   };

//   const result = await GenDrugContinueLogData(controller, data);

//   if (result?.items) {
//     controller.setState({
//       GenerateContinueByPlanSequence: {
//         ...state.GenerateContinueByPlanSequence,
//         paginatedItems,
//         continueList: result.items,
//       },
//     });
//   } else {
//     controller.setState({
//       errorMessage: {
//         ...state.errorMessage,
//         [CARD_NAME]: "Error To Get Log Data!",
//       },
//     });
//   }
// };

export const HandleSearchContinue: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchContinue = state.GenerateContinueByPlanSequence?.searchContinue;
  const paginatedItems = state.GenerateContinueByPlanSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    ...searchContinue,
    limit: pageSize,
    offset,
  };

  const result = await GenDrugContinueLogData(controller, data);

  if (result?.items) {
    controller.setState({
      GenerateContinueByPlanSequence: {
        ...state.GenerateContinueByPlanSequence,
        continueList: result.items,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

export const HandleGenerateContinue: Handler<Params<"GENERATE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GENERATE]: "LOADING",
    },
  });

  const data = {
    plan_orders: params.selectedItems || [],
  };

  const [genResp, genErr] = await GenerateDrugContinue(controller, { data });

  if (genResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: genResp?.detail,
      },
    });

    // Clear Generate Drug Continue Data
    HandleClearContinue(controller, {} as any);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GENERATE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: genErr,
      },
    });
  }
};

export const HandleClearContinue: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    GenerateContinueByPlanSequence: {
      ...state.GenerateContinueByPlanSequence,
      continueList: [],
      searchContinue: {
        ...INIT_DATA,
      },
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });

  // Re-fetch
  HandleSearchContinue(controller, { action: "SEARCH", page: 0, pageSize: LIST_LIMIT });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GenDrugContinueLogData: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DrugContinueLostList.get({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GenerateDrugContinue: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await GenDrugContinueInterfaceData.post({
    apiToken: controller.apiToken,
    data: {
      ...params.data,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, err];
};
